"use client";

import { Box, IconButton, Stack, Typography } from "@mui/material";
import { ArrowLeft, ArrowRight, Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export function FeatureCarousel({ slides }: { slides: React.ReactNode[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const resumeAfterLightbox = useRef(false);
  const hasMultipleSlides = slides.length > 1;
  const goTo = (index: number) =>
    setActiveIndex((index + slides.length) % slides.length);

  useEffect(() => {
    if (!hasMultipleSlides || paused) return;
    const timer = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [hasMultipleSlides, paused, slides.length]);

  useEffect(() => {
    const handleLightbox = (event: Event) => {
      const open = (event as CustomEvent<{ open?: boolean }>).detail?.open;
      if (open) {
        resumeAfterLightbox.current = !paused;
        if (!paused) setPaused(true);
      } else if (resumeAfterLightbox.current) {
        resumeAfterLightbox.current = false;
        setPaused(false);
      }
    };
    window.addEventListener("screenshot-lightbox", handleLightbox);
    return () =>
      window.removeEventListener("screenshot-lightbox", handleLightbox);
  }, [paused]);

  if (!slides.length) return null;

  return (
    <Box sx={{ mt: 2.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.25 }}
      >
        <IconButton
          onClick={() => goTo(activeIndex - 1)}
          aria-label="Slide anterior"
          size="small"
          disabled={!hasMultipleSlides}
          sx={{
            border: "1px solid hsl(var(--border))",
            "&:focus-visible": {
              outline: "2px solid hsl(var(--primary))",
              outlineOffset: 2,
            },
          }}
        >
          <ArrowLeft size={16} aria-hidden="true" />
        </IconButton>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {activeIndex + 1} / {slides.length}
          </Typography>
          {slides.map((_, index) => (
            <Box
              key={index}
              component="button"
              type="button"
              onClick={() => goTo(index)}
              aria-label={`Ir para slide ${index + 1}`}
              aria-current={index === activeIndex ? "true" : undefined}
              sx={{
                width: index === activeIndex ? 20 : 7,
                height: 7,
                p: 0,
                border: 0,
                borderRadius: 99,
                cursor: "pointer",
                bgcolor:
                  index === activeIndex
                    ? "hsl(var(--primary))"
                    : "hsl(var(--border))",
                transition: "width 160ms ease, background-color 160ms ease",
                "&:focus-visible": {
                  outline: "2px solid hsl(var(--primary))",
                  outlineOffset: 2,
                },
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "none",
                },
              }}
            />
          ))}
        </Stack>
        {hasMultipleSlides ? (
          <IconButton
            onClick={() => setPaused((value) => !value)}
            aria-label={paused ? "Continuar carrossel" : "Pausar carrossel"}
            size="small"
            sx={{
              color: "hsl(var(--muted-foreground))",
              opacity: 0.58,
              p: 0.5,
              "&:hover": { opacity: 1 },
              "&:focus-visible": {
                outline: "2px solid hsl(var(--primary))",
                outlineOffset: 2,
              },
            }}
          >
            {paused ? (
              <Play size={14} aria-hidden="true" />
            ) : (
              <Pause size={14} aria-hidden="true" />
            )}
          </IconButton>
        ) : null}
        <IconButton
          onClick={() => goTo(activeIndex + 1)}
          aria-label="Próximo slide"
          size="small"
          disabled={!hasMultipleSlides}
          sx={{
            border: "1px solid hsl(var(--border))",
            "&:focus-visible": {
              outline: "2px solid hsl(var(--primary))",
              outlineOffset: 2,
            },
          }}
        >
          <ArrowRight size={16} aria-hidden="true" />
        </IconButton>
      </Stack>
      <Box sx={{ minWidth: 0 }}>{slides[activeIndex]}</Box>
    </Box>
  );
}
